import { AutocompleteAPI, DeckAPI, FetchAPI, FieldAPI, FieldListAPI, FilterAPI } from '@philips-internal/oswald-ui-components';
import Controller, { OnAfterFetch, OnChange, OnClick, OnMount, OnSubmit } from '@philips-internal/oswald-ui-core/lib/client';
import { ChangeEvent, ClickEvent, FetchEvent, SubmitEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { Inject } from '@odinjs/odin';
import moment from 'moment/moment.js';
import Audit from '../../app/audit';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import { Data } from '../../interfaces/data';
import BaseController from '../BaseController';
import './exam-result-list-controller.css';
import { ISO_DATE_FORMAT } from '@philips-internal/oswald-ui-model/lib/formatters/date/date-utils';
import { APPLICATION_ROUTES } from '../../constants/application-routes';
import UserContext from '../../utils/user-context';
import { Value } from '@philips-internal/oswald-ui-core';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import { ExamResultsAliases } from '../../codegen/constants/aliases';

interface FilterList {
  filterExamClassification: ClassificationItem;
  filterExamStatus: FilterItem;
  filterExamType: FilterItem;
  filterFromDate: string;
}

interface FilterItem {
  key: string;
  value: string;
}

interface ClassificationItem {
  classification: string;
  sequenceNumber: string;
}

interface FilterParams {
  [key: string]: string;
}

@Controller({  component: 'layout-exams-result-list', fragment: 'exam-results'})
class ExamResultListController extends BaseController {

  @Inject('field-list-exam-type')
    examTypeList!: FieldListAPI;

  @Inject('field-list-exam-status')
    examStatusList!: FieldListAPI;

  @Inject('filter-exams-list')
    filterList!: FilterAPI;

  @Inject('filter-field-exam-type')
    filterExamType!: FilterAPI;

  @Inject('filter-field-exam-status')
    filterExamStatus!: FilterAPI;

  @Inject('fetch-exams')
    fetchExams!: FetchAPI;

  @Inject('deck-exams')
    deckExams!: DeckAPI;

  @Inject(ExamResultsAliases.FILTER_FIELD_EXAM_CLASSIFICATION)
    filterExamClassification!: FieldAPI;

  @Inject(ExamResultsAliases.AUTOCOMPLETE_CLASSIFICATION)
    autoCompleteClassification!: AutocompleteAPI;

  autocompleteExamTypeList: AutocompleteAPI[] = [];

  @OnChange(ExamResultsAliases.FILTER_FIELD_EXAM_TYPE)
  onChangeExamType(event: ChangeEvent<string>): void {
    const value = event.value as unknown as FilterItem;
    this.autoCompleteClassification.setValue([]);
    this.autoCompleteClassification.setEnabled('L' !== value?.key);
  }

  @OnMount({ target: 'filter-exams-list' })
  OnFilterMout(): void {
    if(this.filterList.restoreState()) {
      return;
    }
    HttpService.get(APPLICATION_ENDPOINTS.GET_EXAM_DATE_FROM_FILTER, {}).then(({ body }) => {
      const filterFromDate: string = moment(body as string, ISO_DATE_FORMAT).toJSON();
      this.context.set({ initialValue: filterFromDate });

      const record = {
        filterFromDate: moment.utc(filterFromDate).format(ISO_DATE_FORMAT),
      };

      this.filterList.setData(record);
      this.filterList.submit();
    });
  }

  @OnSubmit({ target: 'filter-exams-list' })
  handleSubmitFilter(response: SubmitEvent): void {
    const initialValue = this.context.get('initialValue');
    const record = response.record as FilterList;
    const params = {};

    this.setParamFilter(params, record.filterExamType?.key, 'filterExamType');
    this.setParamFilter(params, record.filterExamStatus?.key, 'filterExamStatus');
    this.setParamFilter(params, record.filterExamClassification?.sequenceNumber, 'filterExamClassification');

    if (record.filterFromDate || initialValue) {
      const date = record.filterFromDate || initialValue as string
      this.setParamFilter(params, moment(date, ISO_DATE_FORMAT).format(ISO_DATE_FORMAT), 'filterFromDate');
    }

    this.context.set({
      params: {
        ...params,
        ...record,
      }
    });

    this.filterList.persistState();
    this.fetchExams.setParameters(params);
    this.fetchExams.forceFetch();
  }

  setParamFilter(params: FilterParams, value: string, field: string): void {
    if (value != null) {
      params[field] = value;
    }
  }

  @OnClick({ target: ['deck-exams'] })
  async onCardDeckClick(event: ClickEvent): Promise<void> {
    if (event && event.value) {
      const { nrSequencia, nrPrescricao, type, dtProcedimento, cdMedico } = event.value;
      const params = this.context.get('params') as FilterList;
      const { patientId } = UserContext;

      const filterExamTypeField = params.filterExamType as FilterItem;
      const filterExamStatusField = params.filterExamStatus as FilterItem;
      const filterFromDate = params.filterFromDate || null;

      const filterExamType = filterExamTypeField && filterExamTypeField.key as string;
      const filterExamStatus = filterExamStatusField && filterExamStatusField.key as string;

      const routeParams = {
        ...params,
        dtProcedimento: dtProcedimento as string,
        filterExamStatus: filterExamStatus as string || 'ALL',
        filterExamType: filterExamType as string || 'ALL',
        filterFromDate: filterFromDate as string,
        patientId: patientId,
      }

      if (type === 'L') {
        // TODO: Refactor to not use query parameters and don't forget to adjust the "pushRouteByOpenModule" method of the "dashboard" fragment and the "pushToUrl" method of the "patient-root" fragment.
        applicationContext.redirect(APPLICATION_ROUTES.EXAM_LAB_DETAILS, {}, {
          searchParams: {
            ...routeParams,
            cdMedico: cdMedico as string,
            patientId: patientId,
          }
        });
      } else {
        // TODO: Refactor to not use query parameters and don't forget to adjust the "pushRouteByOpenModule" method of the "dashboard" fragment and the "pushToUrl" method of the "patient-root" fragment.
        applicationContext.redirect(APPLICATION_ROUTES.EXAM_DETAILS, {}, {
          searchParams: {
            ...routeParams,
            examType: type as string,
            nrPrescricao: nrPrescricao as string,
            nrSequencia: nrSequencia as string,
          }
        });
      }
    }
  }

  @OnAfterFetch({ target: 'fetch-exams' })
  onAfterFetchExams(event: FetchEvent): void {
    Audit.filterExecutedLog(this.filterList.getRecord() as Data, (event.data as []).length);

    const deckData = event.data as Record<string, Value>[];

    deckData.forEach(exam => {
      exam.review = exam.ieReview && exam.ieReview === 'S' ? true : false;
    });

    this.deckExams.setData(deckData);
  }
}

export default ExamResultListController;
